/**
 * Étape 3 « Prérequis Octopus » : quiz de validation + signature de la charte éthique.
 * Non bloquante — utilisée dans le parcours de complétion ET sur la page autonome
 * ouverte depuis l'e-mail (route /prerequis/:token).
 */
import { useState, useEffect } from 'react';
import { CheckCircle, Loader2, ExternalLink, Mail, Brain, FileSignature } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OctopusPrerequisSteps({ token, onAllDone }) {
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/octopus-prerequis/${token}`);
      if (!res.ok) { setError("Ce lien n'est plus valide."); return; }
      setCtx(await res.json());
    } catch {
      setError('Erreur réseau.');
    }
  };

  useEffect(() => { load(); }, [token]);

  const toggle = async (step, checked) => {
    setBusy(step);
    try {
      const res = await fetch(`${API}/api/octopus-prerequis/${token}/${checked ? 'declare' : 'undeclare'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step }),
      });
      if (res.ok) {
        const state = await res.json();
        setCtx(c => ({ ...c, ...state }));
        if (state.all_declared) onAllDone?.();
      }
    } catch {
      setError("Impossible d'enregistrer, réessayez.");
    } finally {
      setBusy('');
    }
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!ctx) return <Loader2 className="animate-spin text-blue-600" size={22} />;

  const steps = [
    {
      key: 'quiz',
      icon: Brain,
      title: 'Passer le quiz de validation Octopus',
      hint: `Note minimum à obtenir : ${ctx.quiz_min_score}/100. Le résultat est transmis directement à Octopus.`,
      url: ctx.quiz_url,
      cta: 'Ouvrir le quiz',
      check: "J'ai passé le quiz",
    },
    {
      key: 'charte',
      icon: FileSignature,
      title: 'Signer la charte éthique Octopus',
      hint: 'Signature électronique Adobe. Une copie signée est envoyée au back office.',
      url: ctx.charte_url,
      cta: 'Signer la charte',
      check: "J'ai signé la charte",
    },
  ];

  return (
    <div className="space-y-4" data-testid="octopus-prerequis">
      <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100 text-[13px] text-blue-800">
        <Mail size={16} className="mt-0.5 flex-shrink-0" />
        <span>
          Ces 2 liens viennent aussi de vous être envoyés <strong>par e-mail</strong>. Si vous fermez
          cette page, vous pourrez terminer cette étape depuis votre boîte mail, quand vous voulez.
        </span>
      </div>

      {steps.map(({ key, icon: Icon, title, hint, url, cta, check }, i) => {
        const done = !!ctx.steps?.[key]?.declared_at;
        return (
          <div key={key} className={`rounded-2xl border p-4 transition-colors duration-200 ${done ? 'border-emerald-300 bg-emerald-50/60' : 'border-gray-200 bg-white'}`}
            data-testid={`prerequis-card-${key}`}>
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${done ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {done ? <CheckCircle size={16} /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 flex items-start gap-1.5">
                  <Icon size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="break-words">{title}</span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{hint}</p>

                <a href={url} target="_blank" rel="noreferrer"
                  className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-3 sm:py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition"
                  data-testid={`prerequis-link-${key}`}>
                  {cta} <ExternalLink size={14} />
                </a>

                <label className="mt-3 flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={done} disabled={busy === key}
                    onChange={(e) => toggle(key, e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid={`prerequis-check-${key}`} />
                  <span className={`text-sm ${done ? 'text-emerald-700 font-medium' : 'text-gray-600'}`}>
                    {busy === key ? 'Enregistrement…' : check}
                  </span>
                </label>
              </div>
            </div>
          </div>
        );
      })}

      {ctx.all_declared && (
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-800"
          data-testid="prerequis-all-done">
          <strong>Tout est bon, merci !</strong> Le back office vérifie votre quiz et votre charte signée,
          puis vous transmettra votre <strong>lien d'affiliation personnel</strong> pour commencer la prospection.
          Pour toute question, contactez le back office via WhatsApp.
        </div>
      )}
    </div>
  );
}
